import { type CSSProperties, useEffect, useState } from 'react';

import classNames from 'classnames';

import { computeColor } from '../utils/colors';
import { getWidthStyle } from '../utils/sizing';

import { backgroundColors } from '../assets/colors';
import { ACCORDION_DEFAULTS } from './Accordion.constants';
import * as styles from './Accordion.css';
import type { AccordionElementProps } from './Accordion.types';

export function AccordionElement(props: AccordionElementProps) {
  const { element, children, attributes, className, isOpen, isLive = false, onClick } = props;

  const {
    width,
    width_max,
    mobile_hide,
    desktop_hide,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    padding,
    background,
    box_shadow,
    gap,
    customAttributes,
    arrowColor,
  } = element.attrs || {};

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      onClick(e);
    } else if (isOpen === undefined) {
      setOpen(!open);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onClick) {
        onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
      } else if (isOpen === undefined) {
        setOpen(!open);
      }
    }
  };

  return (
    <div
      {...customAttributes}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        styles.accordionElement,
        customAttributes?.className,
      )}
      style={
        {
          '--width': getWidthStyle(width, width_max).width,
          '--max-width': getWidthStyle(width, width_max).maxWidth,
          '--gap': gap ?? ACCORDION_DEFAULTS.gap,
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--padding': padding ?? ACCORDION_DEFAULTS.padding,
          '--box-shadow': box_shadow ?? ACCORDION_DEFAULTS.boxShadow,
          '--background-color': computeColor(background, backgroundColors) || ACCORDION_DEFAULTS.background,
          '--arrow-color': computeColor(arrowColor, backgroundColors) || ACCORDION_DEFAULTS.arrowColor,
        } as CSSProperties
      }
      {...attributes}
      data-state={open ? 'open' : 'closed'}
      onClick={handleClick}
      onKeyDown={isLive ? handleKeyDown : undefined}
      role={isLive ? 'button' : undefined}
      tabIndex={isLive ? 0 : undefined}
    >
      {children}
    </div>
  );
}
